import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, PlusSquare } from "lucide-react";

export default function InstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(() => e);
    };
    const onInstalled = () => setInstalled(true);
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {
        /* ignorato */
      }
      setDeferredPrompt(null);
      return;
    }
    setShowHint((s) => !s);
  };

  if (installed) return null;

  return (
    <div className="relative flex flex-col items-center">
      <motion.button
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        data-testid="install-button"
        onClick={handleClick}
        className="px-5 py-2.5 rounded-full bg-white/60 border border-sky-200 text-sky-900/80 text-[11px] tracking-[0.2em] uppercase hover:bg-white hover:text-sky-950 hover:border-sky-400 transition-colors duration-300 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
      >
        <Download className="w-3.5 h-3.5 text-sky-500" />
        Installa l'app
      </motion.button>
      <AnimatePresence>
        {showHint && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setShowHint(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              data-testid="install-hint"
              className="absolute bottom-full mb-4 w-72 z-50 rounded-2xl bg-white/90 backdrop-blur-2xl border border-sky-200 p-4 text-left shadow-2xl"
            >
            <p className="text-xs text-sky-950 font-medium mb-3">
              Come installare Napoli Lounge:
            </p>
            <div className="flex items-start gap-2.5 mb-2.5">
              <Share className="w-3.5 h-3.5 text-sky-500 mt-0.5 shrink-0" />
              <p className="text-[11px] text-sky-900/70 leading-relaxed">
                Su iPhone: premi <span className="text-sky-950 font-semibold">Condividi</span> poi{" "}
                <span className="text-sky-950 font-semibold">Aggiungi a schermata Home</span>
              </p>
            </div>
            <div className="flex items-start gap-2.5">
              <PlusSquare className="w-3.5 h-3.5 text-sky-500 mt-0.5 shrink-0" />
              <p className="text-[11px] text-sky-900/70 leading-relaxed">
                Su Android: apri il menu del browser e premi{" "}
                <span className="text-sky-950 font-semibold">Installa app</span>
              </p>
            </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
